import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import styled from "styled-components";
import { formatDate } from "../../Funcoes/FormatData";
import { AuthContext } from "../../Auth/AuthProvider";
import { VerifyPermission } from "../../Funcoes/VerifyPermission";
import SortableTable from "../../Componentes/Utils/SortableTable";
import styles from "../../Componentes/HomeBody/HomeBody.module.css";

const emptyFilters = {
  nome: "",
  matricula: "",
  funcoes: [],
  encarregados: [],
  obras: [],
  contratos: [],
  status: [],
  maoObra: [],
};

const STATUS_OPTIONS = [
  { value: "ATIVO", label: "ATIVO" },
  { value: "DESLIGADO", label: "DESLIGADO" },
  { value: "AFASTADO", label: "AFASTADO" },
  { value: "FERIAS", label: "FÉRIAS" },
];

const MAO_OPTIONS = [
  { value: "D", label: "DIRETA" },
  { value: "I", label: "INDIRETA" },
];

const TableFlexFill = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  display: flex;
  flex-direction: column;

  & > div {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
`;

const countActiveFilters = (f) => {
  let n = 0;
  if (f.nome.trim()) n += 1;
  if (f.matricula.trim()) n += 1;
  if (f.funcoes.length) n += 1;
  if (f.encarregados.length) n += 1;
  if (f.obras.length) n += 1;
  if (f.contratos.length) n += 1;
  if (f.status.length) n += 1;
  if (f.maoObra.length) n += 1;
  return n;
};

const HomeBodyV2 = ({
  dados,
  funcoes,
  encarregados,
  obras,
  contratos,
  onFilter,
  loading,
  modulo,
  filterOpen,
  onFilterClose,
  onFiltersChange,
}) => {
  const { user } = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const tabelaRef = useRef(null);

  const [linhas, setLinhas] = useState(100);
  const [filtros, setFiltros] = useState(emptyFilters);
  const [draft, setDraft] = useState(emptyFilters);

  const total = dados.length;
  const ativos = dados.filter((obj) => obj.STATUS === "ATIVO").length;
  const desligados = dados.filter((obj) => obj.STATUS === "DESLIGADO").length;
  const afastados = dados.filter((obj) => obj.STATUS === "AFASTADO").length;
  const ferias = dados.filter((obj) => obj.STATUS === "FERIAS").length;
  const diretos = dados.filter((obj) => obj.MAOOBRA === "D").length;
  const indiretos = dados.filter((obj) => obj.MAOOBRA === "I").length;

  const opcoesFuncao = useMemo(
    () => funcoes.map((funcao) => ({ value: funcao, label: funcao })),
    [funcoes]
  );
  const opcoesEncarregado = useMemo(
    () => encarregados.map((enc) => ({ value: enc, label: enc })),
    [encarregados]
  );
  const opcoesObra = useMemo(
    () => obras.map((obra) => ({ value: obra, label: obra })),
    [obras]
  );
  const opcoesContrato = useMemo(
    () => contratos.map((cont) => ({ value: cont, label: cont })),
    [contratos]
  );

  const activeCount = countActiveFilters(filtros);

  useEffect(() => {
    onFilter(
      filtros.nome,
      filtros.matricula,
      filtros.funcoes,
      filtros.encarregados,
      filtros.obras,
      filtros.contratos,
      filtros.status,
      filtros.maoObra
    );
  }, [filtros, onFilter]);

  useEffect(() => {
    onFiltersChange?.(activeCount);
  }, [activeCount, onFiltersChange]);

  useEffect(() => {
    if (filterOpen) setDraft(filtros);
  }, [filterOpen, filtros]);

  useEffect(() => {
    const element = tabelaRef.current;
    if (!element) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = element;
      if (scrollTop + clientHeight >= scrollHeight - 10) {
        setLinhas((prev) => prev + 100);
      }
    };

    element.addEventListener("scroll", handleScroll);
    return () => element.removeEventListener("scroll", handleScroll);
  }, []);

  const viewColaboradores = () => {
    if (modulo === "rh") return true;
    return VerifyPermission(user, { modulo: "ssma", funcao: "view_colab" });
  };

  const openColab = (matricula) => {
    if (modulo === "rh") {
      window.open(`/modulo/${modulo}/colaborador/${matricula}`, "_blank");
      return;
    }
    if (modulo === "ssma") {
      if (VerifyPermission(user, { modulo: "ssma", funcao: "access_colab" })) {
        window.open(`/modulo/${modulo}/colaborador/${matricula}`, "_blank");
      }
      return;
    }
    if (modulo === "qualidade") {
      if (VerifyPermission(user, { modulo: "qualidade", funcao: "access_colab" })) {
        window.open(`/modulo/${modulo}/colaborador/${matricula}`, "_blank");
      }
    }
  };

  const aplicarFiltros = (next) => {
    setFiltros(next);
    onFilterClose?.();
  };

  const limparFiltros = () => {
    setDraft(emptyFilters);
    aplicarFiltros(emptyFilters);
  };

  const setDraftKey = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));

  const columns = [
    {
      id: "pendencia",
      header: "",
      accessor: () => null,
      sortable: false,
      width: "36px",
      align: "center",
      render: (_value, row) => (
        <div className={row.DADOS === "PENDENTE" ? styles.colabPend : styles.colabOk} />
      ),
    },
    { id: "nome", header: "COLABORADOR", accessor: (r) => r.NOME },
    { id: "matricula", header: "MATRICULA", accessor: (r) => r.MATRICULA },
    { id: "funcao", header: "FUNÇÃO", accessor: (r) => r.FUNCAO },
    { id: "encarregado", header: "ENCARREGADO", accessor: (r) => r.IMEDIATO ?? "-" },
    {
      id: "admissao",
      header: "ADMISSÃO",
      accessor: (r) => r.ADMISSAO,
      sortValue: (r) => r.ADMISSAO,
      render: (v) => formatDate(v),
    },
    {
      id: "demissao",
      header: "DEMISSÃO",
      accessor: (r) => (r.DEMISSAO !== "        " ? r.DEMISSAO : null),
      sortValue: (r) => (r.DEMISSAO !== "        " ? r.DEMISSAO : null),
      render: (v) => (v ? formatDate(v) : "-"),
    },
    { id: "dias", header: "DIAS TRAB.", accessor: (r) => r.DIAS_TRAB, align: "left" },
    { id: "cc", header: "CENTRO CUSTO", accessor: (r) => r.CENTRO_CUSTO },
    { id: "contrato", header: "CONTRATO", accessor: (r) => r.CONTRATO ?? "-" },
    { id: "mo", header: "MO", accessor: (r) => r.MAOOBRA, width: "56px" },
    { id: "status", header: "STATUS", accessor: (r) => r.STATUS },
  ];

  const multiField = (label, options, key) => (
    <Autocomplete
      multiple
      options={options}
      value={options.filter((opt) => draft[key].includes(opt.value))}
      onChange={(_, selected) =>
        setDraftKey(
          key,
          selected.map((opt) => opt.value)
        )
      }
      getOptionLabel={(opt) => opt.label}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      renderInput={(params) => <TextField {...params} label={label} />}
      fullWidth
      size="small"
      limitTags={isMobile ? 1 : 2}
    />
  );

  if (!viewColaboradores()) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary">
          Você não possui permissão para visualizar colaboradores neste módulo.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        p: { xs: 1.25, md: 1.5 },
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 1.25, md: 1.5 },
          borderRadius: 2,
          flexShrink: 0,
          bgcolor: (t) =>
            t.palette.mode === "dark" ? alpha("#fff", 0.03) : alpha("#152A36", 0.02),
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
            <Chip size="small" label={`${total} colaboradores`} color="primary" variant="outlined" />
            <Chip size="small" label={`${ativos} ativos`} />
            <Chip size="small" label={`${desligados} desligados`} />
            <Chip size="small" label={`${afastados} afastados`} sx={{ display: { xs: "none", sm: "inline-flex" } }} />
            <Chip size="small" label={`${ferias} férias`} sx={{ display: { xs: "none", sm: "inline-flex" } }} />
            <Chip size="small" label={`${diretos} direto / ${indiretos} indireto`} sx={{ display: { xs: "none", md: "inline-flex" } }} />
          </Stack>

        </Stack>

        {activeCount > 0 && (
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
            {filtros.nome.trim() && (
              <Chip
                size="small"
                label={`Nome: ${filtros.nome}`}
                onDelete={() => setFiltros((f) => ({ ...f, nome: "" }))}
              />
            )}
            {filtros.matricula.trim() && (
              <Chip
                size="small"
                label={`Matrícula: ${filtros.matricula}`}
                onDelete={() => setFiltros((f) => ({ ...f, matricula: "" }))}
              />
            )}
            {filtros.funcoes.length > 0 && (
              <Chip
                size="small"
                label={`Função (${filtros.funcoes.length})`}
                onDelete={() => setFiltros((f) => ({ ...f, funcoes: [] }))}
              />
            )}
            {filtros.encarregados.length > 0 && (
              <Chip
                size="small"
                label={`Encarregado (${filtros.encarregados.length})`}
                onDelete={() => setFiltros((f) => ({ ...f, encarregados: [] }))}
              />
            )}
            {filtros.obras.length > 0 && (
              <Chip
                size="small"
                label={`Centro custo (${filtros.obras.length})`}
                onDelete={() => setFiltros((f) => ({ ...f, obras: [] }))}
              />
            )}
            {filtros.contratos.length > 0 && (
              <Chip
                size="small"
                label={`Contrato (${filtros.contratos.length})`}
                onDelete={() => setFiltros((f) => ({ ...f, contratos: [] }))}
              />
            )}
            {filtros.status.length > 0 && (
              <Chip
                size="small"
                label={`Status (${filtros.status.length})`}
                onDelete={() => setFiltros((f) => ({ ...f, status: [] }))}
              />
            )}
            {filtros.maoObra.length > 0 && (
              <Chip
                size="small"
                label={`Mão de obra (${filtros.maoObra.length})`}
                onDelete={() => setFiltros((f) => ({ ...f, maoObra: [] }))}
              />
            )}
            <Chip
              size="small"
              color="secondary"
              variant="outlined"
              label="Limpar filtros"
              onClick={limparFiltros}
            />
          </Stack>
        )}
      </Paper>

      <Paper
        variant="outlined"
        sx={{
          flex: 1,
          minHeight: 0,
          borderRadius: 2,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          bgcolor: "#242D34",
        }}
      >
        <TableFlexFill>
          <SortableTable
            ref={tabelaRef}
            columns={columns}
            data={dados}
            limit={linhas}
            isLoading={loading}
            loadingText="Carregando colaboradores..."
            onRowClick={(row) => openColab(row.MATRICULA)}
            rowCursor="pointer"
            emptyText="NENHUM COLABORADOR ENCONTRADO"
            initialSort={{ key: "nome", direction: "asc" }}
          />
        </TableFlexFill>
      </Paper>

      <Dialog
        open={Boolean(filterOpen)}
        onClose={onFilterClose}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            pr: 1,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700} component="span">
              Filtros
            </Typography>
            <Typography variant="body2" color="text.secondary" display="block">
              Refine a lista de colaboradores
            </Typography>
          </Box>
          <IconButton aria-label="Fechar filtros" onClick={onFilterClose} edge="end">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              label="Colaborador"
              value={draft.nome}
              onChange={(e) => setDraftKey("nome", e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Matrícula"
              value={draft.matricula}
              onChange={(e) => setDraftKey("matricula", e.target.value)}
              fullWidth
              size="small"
            />
            {multiField("Função", opcoesFuncao, "funcoes")}
            {multiField("Encarregado", opcoesEncarregado, "encarregados")}
            {multiField("Centro de custo", opcoesObra, "obras")}
            {multiField("Contrato", opcoesContrato, "contratos")}
            {multiField("Status", STATUS_OPTIONS, "status")}
            {multiField("Mão de obra", MAO_OPTIONS, "maoObra")}
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2,
            py: 1.5,
            gap: 1,
            flexDirection: isMobile ? "column-reverse" : "row",
            "& > :not(style)": { m: 0 },
          }}
        >
          <Button
            fullWidth={isMobile}
            onClick={() => {
              setDraft(emptyFilters);
              aplicarFiltros(emptyFilters);
            }}
            sx={{ minHeight: 44 }}
          >
            Limpar
          </Button>
          <Button
            fullWidth={isMobile}
            variant="contained"
            color="secondary"
            onClick={() => aplicarFiltros(draft)}
            sx={{ minHeight: 44 }}
          >
            Aplicar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HomeBodyV2;
